import { Box, Button, Group, Text } from "@mantine/core";
import { useInterval } from "@mantine/hooks";
import { SendRounded } from "@mui/icons-material";
import { MainMetaMapSubTopic } from "@tsTypes/meta-schema";
import { Question } from "@tsTypes/question-schema";
import { QuestionTestStateSchema } from "@tsTypes/redux-schema";
import { AverageSubjectGradeBoundariesSchema } from "@tsTypes/subject-schema";
import { CustomTest, MockExam, Test, TestStates } from "@tsTypes/test-schema";
import { averageArray, checkIfMultipleChoiceQuestion } from "@utils/helpers";
import { useRouter } from "next/navigation";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import {
  selectTestAllQuestionStates,
  updateTestQuestionState,
} from "../../../../../../../../features/activeTestSessions/activeTestSessionsSlice";
import { customTestAttempt } from "../../../../../../../../features/progress/customTestUserReduxMetadataSlice";
import { mockExamAttempt } from "../../../../../../../../features/progress/mockExamUserReduxMetadataSlice";
import { topicTestAttempt } from "../../../../../../../../features/progress/topicTestUserReduxMetadataSlice";
import {
  useCustomDispatch,
  useCustomSelector,
} from "../../../../../../../../lib/redux/hooks";
import { useMainMetaMap } from "../../../../../../../components/CoreDataProvider";
import { setNotification } from "../../../../../../../components/notifications";
import { submitMcAnswerMark } from "../../../questions/[topic]/components/QuestionModule";
import QuestionQuickJumpCard from "./QuestionQuickJumpCard";
import TestActionsDropdown from "./TestActionsDropdown";
import TestClock from "./TestClock";
import TestMarkingLegend from "./TestMarkingLegend";
import classes from "./TestMetrics.module.css";
import TestPerformanceBreakdown from "./TestPerformanceBreakdown";
import TestPerformanceSummary from "./TestPerformanceSummary";
import TestStatusTitle from "./TestStatusTitle";

export default function TestMetrics({
  testStatus,
  setTestStatus,
  subjectNameLower,
  subjectDocumentId,
  testMeta,
  setCurrentQuestion,
  currentQuestion,
  testMarks,
  attemptedQuestions,
  flaggedQuestions,
  currentSeconds,
  updateTimer,
  subjectGradeBoundaries,
  shuffledQuestions,
}: {
  testStatus: TestStates;
  setTestStatus: Dispatch<SetStateAction<TestStates>>;
  subjectNameLower: string;
  subjectDocumentId: string;
  testMeta: CustomTest | MockExam | Test;
  setCurrentQuestion: Dispatch<SetStateAction<number>>;
  currentQuestion: number;
  testMarks: { [questionId: string]: number | undefined };
  attemptedQuestions: string[];
  flaggedQuestions: string[];
  currentSeconds: number;
  updateTimer: (timerUpdates: { currentSeconds?: number }) => void;
  subjectGradeBoundaries: AverageSubjectGradeBoundariesSchema[string];
  shuffledQuestions?: Question[];
}) {
  const mainMetaMap = useMainMetaMap();
  const questions = shuffledQuestions ?? testMeta.RelatedQuestions;
  const isCustomTestOrMockExam = "TestId" in testMeta;
  const adjustedTestId = isCustomTestOrMockExam
    ? testMeta.TestId
    : testMeta.documentId;
  const router = useRouter();
  const dispatch = useCustomDispatch();
  const totalSeconds = getTestTotalSeconds(
    testMeta.Breakdown.mcMarks,
    testMeta.Breakdown.laMarks
  );
  const currentSecondsRef = useRef(currentSeconds);

  const allQuestionStates = useCustomSelector((state) =>
    selectTestAllQuestionStates(state, adjustedTestId)
  );
  useEffect(() => {
    currentSecondsRef.current = currentSeconds;
  }, [currentSeconds]);
  const interval = useInterval(() => {
    const latestSeconds = currentSecondsRef.current;
    const newSeconds = latestSeconds + 1;
    generateTimeProgresionNotifications(latestSeconds, totalSeconds);
    updateTimer({ currentSeconds: newSeconds });

    if (newSeconds >= totalSeconds) {
      interval.stop();
      setTestStatus("marking");
    }
  }, 1000);

  useEffect(() => {
    if (testStatus === "taking") {
      return interval.start();
    } else {
      interval.stop();
    }
    if (testStatus === "marking") {
      questions.forEach((q) => {
        if (allQuestionStates[q.documentId]?.marks !== undefined) {
          return;
        }
        const isMultipleChoiceQuestion = checkIfMultipleChoiceQuestion(q);
        if (isMultipleChoiceQuestion) {
          const selectedOption =
            allQuestionStates[q.documentId]?.selectedOption;
          const relevantSubTopicDocumentId = q.RelatedSubTopics.find((s) =>
            Object.keys(testMeta.RelatedSubTopics).find(
              (subTopicId) => subTopicId === s.documentId
            )
          )!.documentId;
          dispatch(
            updateTestQuestionState({
              testId: adjustedTestId,
              questionId: q.documentId,
              questionState: {
                marks:
                  q.QuestionStyle[0].CorrectOption?.CorrectOption ===
                  selectedOption
                    ? 1
                    : 0,
              },
            })
          );
          if (selectedOption !== undefined) {
            submitMcAnswerMark(
              false,
              subjectDocumentId,
              (mainMetaMap[relevantSubTopicDocumentId] as MainMetaMapSubTopic)
                .topicDocumentId,
              relevantSubTopicDocumentId,
              q.documentId,
              selectedOption,
              q.QuestionStyle[0].CorrectOption?.CorrectOption,
              dispatch
            );
          }
        } else {
          dispatch(
            updateTestQuestionState({
              testId: adjustedTestId,
              questionId: q.documentId,
              questionState: {
                marks: !attemptedQuestions.includes(q.documentId)
                  ? 0
                  : undefined,
              },
            })
          );
        }
      });
    }
  }, [testStatus]);

  useEffect(() => {
    if (
      testStatus === "marking" &&
      Object.values(allQuestionStates).filter(
        (q: QuestionTestStateSchema) => q.marks !== undefined
      ).length === questions.length
    ) {
      updateTestStatusAndScrollToTop("reviewing");
      const percentagePerformance = averageArray(
        questions.map((q) => getQuestionPercentagePerformance(testMarks, q))
      )!;
      if (isCustomTestOrMockExam && testMeta.type === "customTest") {
        dispatch(
          customTestAttempt({
            subjectDocumentId,
            customTestId: adjustedTestId,
            laMarks: testMeta.Breakdown.laMarks,
            mcMarks: testMeta.Breakdown.mcMarks,
            numQuestions: testMeta.Breakdown.numQuestions,
            includedSubTopics: Object.keys(testMeta.RelatedSubTopics),
            percentagePerformance,
            percentageFree: getPercentageFree(currentSeconds, totalSeconds),
            date: Date.now(),
          })
        );
      } else if (isCustomTestOrMockExam && testMeta.type === "mockExam") {
        dispatch(
          mockExamAttempt({
            subjectDocumentId,
            mockExamId: adjustedTestId,
            laMarks: testMeta.Breakdown.laMarks,
            mcMarks: testMeta.Breakdown.mcMarks,
            numQuestions: testMeta.Breakdown.numQuestions,
            includedSubTopics: Object.keys(testMeta.RelatedSubTopics),
            percentagePerformance,
            percentageFree: getPercentageFree(currentSeconds, totalSeconds),
            date: Date.now(),
          })
        );
      } else {
        dispatch(
          topicTestAttempt({
            subjectDocumentId,
            topicDocumentId: testMeta.relatedTopicDocumentId,
            testDocumentId: adjustedTestId,
            testNumber: testMeta.Number,
            date: Date.now(),
            percentagePerformance,
            percentageFree: getPercentageFree(currentSeconds, totalSeconds),
          })
        );
      }
    }
  }, [allQuestionStates]);

  function updateTestStatusAndScrollToTop(testStatus: TestStates) {
    if (currentQuestion != 0) {
      setCurrentQuestion(0);
    }
    setTestStatus(testStatus);
    router.push("#");
  }

  return (
    <>
      <Box className={classes.headerWrapper} id="test-metrics">
        {isCustomTestOrMockExam && (
          <Text
            c="dark.1"
            ta="center"
            size="sm"
            fw={500}
            pos="absolute"
            top={"-1.5rem"}
            left={"50%"}
            style={{
              transform: "translateX(-50%)",
              width: "100%",
            }}
          >
            {`Test ID: ${adjustedTestId}`}
          </Text>
        )}
        <TestStatusTitle
          testStatus={testStatus}
          questionCount={questions.length}
          attemptedQuestionCount={attemptedQuestions.length}
          testMarks={testMarks}
        />
        {testStatus === "paused" || testStatus === "taking" ? (
          <Group gap="md" className={classes.clockGroup}>
            <TestClock
              testStatus={testStatus}
              setTestStatus={setTestStatus}
              totalSeconds={totalSeconds}
              currentSeconds={currentSeconds}
            />
            <Button
              leftSection={<SendRounded style={{ width: 18, height: 18 }} />}
              onClick={() => updateTestStatusAndScrollToTop("marking")}
              disabled={!attemptedQuestions.length}
              color={attemptedQuestions.length ? "correct" : "gray"}
            >
              Mark
            </Button>
          </Group>
        ) : testStatus === "marking" ? (
          <TestMarkingLegend
            questionCount={questions.length}
            testMarks={testMarks}
            onAllMarkingComplete={() => setTestStatus("reviewing")}
          />
        ) : (
          <TestPerformanceSummary
            totalMarks={testMeta.Breakdown.mcMarks + testMeta.Breakdown.laMarks}
            achievedMarks={Object.values(testMarks)
              .filter((marks) => marks !== undefined)
              .reduce((acc, mark) => acc + mark, 0)}
            timeSpent={currentSeconds}
            totalTime={totalSeconds}
            subjectGradeBoundaries={subjectGradeBoundaries}
          />
        )}
        <TestActionsDropdown
          testId={adjustedTestId}
          hasAttemptedQuestions={attemptedQuestions.length > 0}
          hasStartedClock={currentSeconds > 0}
          subjectNameLower={subjectNameLower}
          setTestStatus={setTestStatus}
          testStatus={testStatus}
          shareable={isCustomTestOrMockExam ? true : undefined}
        />
        {testStatus === "reviewing" && (
          <TestPerformanceBreakdown
            testMeta={testMeta}
            testMarks={testMarks}
            subjectNameLower={subjectNameLower}
          />
        )}
        <div className={classes.navigationWrapper}>
          {questions.map((q, index) => {
            return (
              <QuestionQuickJumpCard
                currentQuestion={currentQuestion}
                setCurrentQuestion={setCurrentQuestion}
                key={q.documentId}
                index={index}
                isAttempted={
                  attemptedQuestions.includes(q.documentId) ? true : false
                }
                isMarked={testMarks[q.documentId] !== undefined ? true : false}
                isFlagged={
                  flaggedQuestions.includes(q.documentId) ? true : false
                }
                testStatus={testStatus}
                percentagePerformance={
                  testStatus === "reviewing"
                    ? getQuestionPercentagePerformance(testMarks, q)
                    : undefined
                }
                isMcQuestion={checkIfMultipleChoiceQuestion(q)}
              />
            );
          })}
        </div>
      </Box>
    </>
  );
}

function generateTimeProgresionNotifications(
  currentSeconds: number,
  totalSeconds: number
) {
  const progressStatus = getTestTimeProgressionStatus(
    currentSeconds,
    totalSeconds
  );

  if (
    progressStatus === "halfWay" &&
    currentSeconds === Math.floor(totalSeconds / 2)
  ) {
    setNotification(
      "Test",
      false,
      "You are half-way through your test time!",
      "success"
    );
  } else if (
    progressStatus === "nearEnd" &&
    currentSeconds === Math.floor(totalSeconds * 0.9)
  ) {
    setNotification(
      "Test",
      false,
      "You have less than 10% of your test time left!",
      "success"
    );
  }
}

export function getTestTotalSeconds(mcMarks: number, laMarks: number) {
  return getTestMcSeconds(mcMarks) + getTestLaSeconds(laMarks);
}

export function getTestMcSeconds(mcMarks: number) {
  return mcMarks * 1.5 * 60;
}

export function getTestLaSeconds(laMarks: number) {
  return laMarks * 1.5 * 60;
}

export function getTestMcMarks(mcSeconds: number) {
  return mcSeconds / (1.5 * 60);
}

export function getTestLaMarks(laSeconds: number) {
  return laSeconds / (1.5 * 60);
}

export function getTestTimeProgressionStatus(
  currentSeconds: number,
  totalSeconds: number
) {
  const halfWay = totalSeconds * 0.5;
  const nearEnd = totalSeconds * 0.1;
  if (currentSeconds > halfWay && currentSeconds <= halfWay + 1) {
    return "halfWay";
  } else if (currentSeconds > nearEnd && currentSeconds <= nearEnd + 1) {
    return "nearEnd";
  }
}

function getQuestionPercentagePerformance(
  testMarks: { [questionId: string]: number | undefined },
  question: Question
) {
  return (testMarks[question.documentId]! / question.TotalMarks) * 100;
}

function getPercentageFree(currentSeconds: number, totalSeconds: number) {
  return Math.floor(100 - (currentSeconds / totalSeconds) * 100);
}
